import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import CloseIcon from "../../assets/icons/close-icon-black.svg";
import errorIcon from "../../assets/icons/error-icon.svg";
import searchIcon from "../../assets/icons/search-icon.svg";
import { useGetFolderAssign } from "../../services/segment/index";
import { PrimaryButton, CircularProgressCustom, BorderSecondary } from "../../utils/muiCustomTheme";
import { FolderTree } from "./FolderTree";
import { ParentTree, SegmentDialogProps } from "./segment-interface";
import { useCountryList } from "../../services/okta";

const SegmentDialog = ({ open, onClose, actionType, onSegmentSelect, onTagSelect }: SegmentDialogProps) => {
  const methods = useFormContext();
  const { getValues, control, watch } = methods;

  const { data: countries, isLoading: isCountriesLoading } = useCountryList();

  const countryTags = Array.from(new Set(countries?.flatMap((c) => c.tags) || []));

  const { data: dataFolderAssign, isPending: isPendingDataFolderAssign } = useGetFolderAssign({
    query: {
      tags: countryTags
    },
    options: {
      enabled: !isCountriesLoading && countryTags.length > 0,
    },
  });
  const isTall = useMediaQuery("(min-height: 900px)");
  const isMedium = useMediaQuery("(min-height: 700px) and (max-height: 899px)");
  const isLow = useMediaQuery("(min-height: 490px) and (max-height: 699px)");
  const isVeryLow = useMediaQuery("(max-height: 489px)");

  let maxHeight = 400;
  if (isTall) maxHeight = 400;
  if (isMedium) maxHeight = 280;
  if (isLow) maxHeight = 150;
  if (isVeryLow) maxHeight = 100;

  const rawAudienceTree = useMemo(() => {
    if (!Array.isArray(dataFolderAssign?.data)) return [];
    return dataFolderAssign?.data as ParentTree[];
  }, [dataFolderAssign?.data]);

  const selectedPath = [watch("country"), watch("brand"), watch("folder")].filter(Boolean);

  if (isPendingDataFolderAssign) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgressCustom />
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} className="general-dialog">
      <DialogTitle className="custom-dialog-title">
        <div className="view-all-attributes__title">
          Choose folder to save {actionType === "saveSegment" ? "segment" : "campaign"}
        </div>
        <img className="close-icon" src={CloseIcon} alt="close icon" onClick={onClose} />
      </DialogTitle>
      <DialogContent className="dialog-window">
        <div className="dataset-sec">
          <div className="view-all-attributes__dataset-wrapper__label">
            You are saving the {actionType === "saveSegment" ? "segment" : "campaign"} within
            Market:
          </div>
          <Box className="dataset">{getValues("country")}</Box>
        </div>
        {actionType === "pickSegment" && (
          <form>
            <div className="form-control-container">
              <label className="form-control-label">
                <Controller
                  control={control}
                  rules={{
                    required: "Please provide segment name",
                  }}
                  name="searchSegmentName"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <>
                        <TextField
                          {...field}
                          className="form-control-container__text-field"
                          margin="dense"
                          fullWidth
                          variant="outlined"
                          placeholder="Search segment"
                          onChange={(e) => field.onChange(e.target.value.toLowerCase())}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <img src={searchIcon} alt="loop" />
                                </InputAdornment>
                              ),
                            },
                          }}
                        />

                        {error?.message && (
                          <div className="create-segment__input-wrap__segment-err">
                            <img src={errorIcon} alt="Error Icon" />
                            {error?.message}
                          </div>
                        )}
                      </>
                    );
                  }}
                />
              </label>
            </div>
          </form>
        )}
        <Box
          className="folder-structure__container"
          sx={{
            maxHeight: maxHeight,
            overflowY: "auto",
          }}
        >
          <FolderTree data={rawAudienceTree} defaultSelectedPath={selectedPath}
            setSelectedTag={(tag) => {
              if (onTagSelect) {
                onTagSelect(tag); //pass selected tag to parent
              }
            }}
          />
        </Box>

        <Box component="div" className="folder-placement-rules">
          <b>Folder Placement Rules:</b>
          <ul>
            <li>
              Please{" "}
              <span className="text-[#E70070]">
                <b>choose</b>
              </span>{" "}
              the folder before{" "}
              <span className="text-[#E70070]">
                <b>Save</b>
              </span>
            </li>
            <li>
              Once a {actionType === "saveSegment" ? "segment" : "campaign"} is placed in a folder,
              it cannot be moved to another folder.
            </li>
            <li>
              {actionType === "saveSegment" ? "Segment" : "Campaign"}s can only be saved in the
              lowest level of the folder hierarchy.
            </li>
          </ul>
        </Box>
      </DialogContent>
      <DialogActions>
        <BorderSecondary type="button" onClick={onClose}>
          Cancel
        </BorderSecondary>

        <PrimaryButton
          type="button"
          disabled={!watch("path") ? true : false}
          className={`${!watch("path") ? "!cursor-not-allowed" : ""}`}
          onClick={() => {
            onClose();
            onSegmentSelect();
          }}
        >
          {actionType === "saveSegment" ? "Save" : "Save and Activate"}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default SegmentDialog;
